import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, TrendingUp, TrendingDown } from 'lucide-react'
import type { PartyDistrictStateWithDistrict } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'

interface PartyStrengthSummaryProps {
  partyId: string
  districtStates: PartyDistrictStateWithDistrict[]
  isLoading?: boolean
  className?: string
}

interface DistrictSummary {
  name: string
  region: string
  recognition: number
}

export function PartyStrengthSummary({
  partyId,
  districtStates,
  isLoading = false,
  className,
}: PartyStrengthSummaryProps) {
  const stats = useMemo(() => {
    if (!districtStates || districtStates.length === 0) {
      return {
        avgRecognition: 0,
        avgOrganization: 0,
        strongest: [] as DistrictSummary[],
        weakest: [] as DistrictSummary[],
        totalDistricts: 0,
      }
    }

    const totalDistricts = districtStates.length

    // Calculate averages
    const avgRecognition = Math.round(
      districtStates.reduce((sum, s) => sum + s.recognition, 0) / totalDistricts
    )
    const avgOrganization = Math.round(
      districtStates.reduce((sum, s) => sum + s.organization_strength, 0) / totalDistricts
    )

    // Sort by recognition for strongest/weakest
    const sorted = [...districtStates].sort((a, b) => b.recognition - a.recognition)

    const toSummary = (state: PartyDistrictStateWithDistrict): DistrictSummary => ({
      name: state.district?.name || 'Unknown',
      region: state.district?.region?.name || 'Unknown',
      recognition: state.recognition,
    })

    const strongest = sorted.slice(0, 3).map(toSummary)
    const weakest = sorted.slice(-3).reverse().map(toSummary)

    return {
      avgRecognition,
      avgOrganization,
      strongest,
      weakest,
      totalDistricts,
    }
  }, [districtStates])

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">District Strength</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 animate-pulse">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-2 bg-muted rounded" />
            <div className="h-4 bg-muted rounded w-2/3" />
            <div className="h-2 bg-muted rounded" />
            <div className="h-20 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (stats.totalDistricts === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">District Strength</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center space-y-3">
            <div className="rounded-full bg-muted p-3">
              <MapPin className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">No district data</p>
              <p className="text-sm text-muted-foreground">
                District states will appear after party creation
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">District Strength</CardTitle>
          <Link
            to={`/parties/${partyId}/districts`}
            className="text-sm text-primary hover:underline"
          >
            View All
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Averages */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>Avg Recognition</span>
              <span className="font-medium">{stats.avgRecognition}%</span>
            </div>
            <Progress value={stats.avgRecognition} className="h-2" />
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>Avg Organization</span>
              <span className="font-medium">{stats.avgOrganization}%</span>
            </div>
            <Progress value={stats.avgOrganization} className="h-2" />
          </div>
        </div>

        {/* Strongest / Weakest */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
          {/* Strongest */}
          <div className="space-y-2">
            <div className="flex items-center gap-1 text-sm font-medium text-green-600">
              <TrendingUp className="h-4 w-4" />
              <span>Strongest</span>
            </div>
            <ul className="space-y-1 text-sm">
              {stats.strongest.map((d, i) => (
                <li key={i} className="flex justify-between">
                  <span className="truncate text-muted-foreground" title={d.name}>
                    {d.name}
                  </span>
                  <span className="ml-1 font-medium">{d.recognition}%</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Weakest */}
          <div className="space-y-2">
            <div className="flex items-center gap-1 text-sm font-medium text-red-600">
              <TrendingDown className="h-4 w-4" />
              <span>Weakest</span>
            </div>
            <ul className="space-y-1 text-sm">
              {stats.weakest.map((d, i) => (
                <li key={i} className="flex justify-between">
                  <span className="truncate text-muted-foreground" title={d.name}>
                    {d.name}
                  </span>
                  <span className="ml-1 font-medium">{d.recognition}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="text-xs text-muted-foreground text-center pt-2 border-t">
          {stats.totalDistricts} districts total
        </div>
      </CardContent>
    </Card>
  )
}
